import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { CandidateExamService } from "../../service/assessSections/CandidateExamService";
import { testService } from "../../service/candidate/testService";
import { candidateService } from "../../service/settings/candidate/candidateService";
import { gradeService } from "../../service/candidate/gradeService";
import {
  getMainQuestions,
  getSubQuestionMap,
  groupQuestionsByPartAndSection,
  sortQuestionsForExam,
  getQuestionId,
} from "../../pages/assessSections/ExamQuestionGrouping";
import { getSubmissionAttachments } from "../../pages/assessSections/helpers/submissionAnswers";

const ExamGradingContext = createContext(null);

const QUESTION_TYPES = {
    MCQ: "MCQ",
    DESCRIPTIVE: "Descriptive",
    SUBMISSION: "Submission",
    SUBQUESTION: "Sub Question",
};

function getMcqQuestionPrompt(questionContent) {
    try {
        const parsed =
            typeof questionContent === "string"
                ? JSON.parse(questionContent || "[]")
                : questionContent;

        if (Array.isArray(parsed)) {
            return JSON.stringify([parsed[0]]);
        }

        return typeof parsed === "string" ? parsed : JSON.stringify([parsed]);
    } catch {
        return questionContent;
    }
}

function parseExamQuestions(examQuestionsString) {
    try {
        const parsed = JSON.parse(examQuestionsString);
        return parsed?.questions || [];
    } catch {
        return [];
    }
}

function getQuestionMix(questions = []) {
    const gradableQuestions = questions.filter((q) => {
        const marks = Number(q.mark_allocation || 0);

        // Child subquestions are real gradable questions.
        if (q.is_subquestion === true || q.is_subquestion === "true") {
            return marks > 0;
        }

        // Normal MCQ / descriptive / submission questions are gradable.
        if (q.question_type !== QUESTION_TYPES.SUBQUESTION) {
            return marks > 0;
        }

        // Parent "Sub Question" wrapper is not gradable.
        return false;
    });

    const hasMcq = gradableQuestions.some(
        (q) => q.question_type === QUESTION_TYPES.MCQ
    );

    const hasManual = gradableQuestions.some(
        (q) => q.question_type !== QUESTION_TYPES.MCQ
    );

    const totalMarkAllocation = gradableQuestions.reduce(
        (sum, q) => sum + Number(q.mark_allocation || 0),
        0
    );

    return {
        hasMcq,
        hasManual,
        isMcqOnly: hasMcq && !hasManual,
        isManualOnly: !hasMcq && hasManual,
        isMixed: hasMcq && hasManual,
        totalMarkAllocation,
    };
}

function applyDefaultPartSectionFallback(questions = []) {
    return questions.map((question) => ({
        ...question,

        section_id: question.section_id || "default-section",
        section_title: question.section_title || "Questions",

        part_id: question.part_id || "default-part",
        part_name: question.part_name || "Part 1",

        section_display_order: question.section_display_order || 1,
        part_display_order: question.part_display_order || 1,
    }));
}

function parseAnswerContent(value) {
    try {
        return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
        return null;
    }
}

function buildCandidateAnswerMap(candidateAnswers = []) {
    return candidateAnswers.reduce((map, answer) => {
        map[answer.question_id] = answer;
        return map;
    }, {});
}

function getMcqSelectedIndexes(candidateAnswer) {
    try {
        const parsed =
            typeof candidateAnswer?.answer_content === "string"
                ? JSON.parse(candidateAnswer.answer_content)
                : candidateAnswer?.answer_content;

        return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
        return [];
    }
}

function getMcqCorrectIndexes(answerContent) {
    return String(answerContent || "")
        .split(",")
        .map((value, index) => ({
            index,
            percentage: Number(value),
        }))
        .filter(
            (item) =>
                item.percentage &&
                item.percentage !== 0 &&
                !Number.isNaN(item.percentage)
        )
        .map((item) => item.index);
}

function calculateMcqMarks({ question, candidateAnswer, answerContent }) {
    const selectedAnswers = getMcqSelectedIndexes(candidateAnswer);

    const answerPercentages = String(answerContent || "")
        .split(",")
        .map((item) => Number(item));

    const numberOfCorrectAnswers = answerPercentages.filter(
        (item) => item && item !== 0 && !Number.isNaN(item)
    ).length;

    if (selectedAnswers.length > numberOfCorrectAnswers) {
        return 0;
    }

    return selectedAnswers.reduce((total, answerIndex) => {
        const percentage = Number(answerPercentages[answerIndex] || 0);
        return total + (percentage * Number(question.mark_allocation || 0)) / 100;
    }, 0);
}
function parseDraftRaw(raw) {
    try {
        return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
        return null;
    }
}

function getDraftText(raw) {
    try {
        const parsed = parseDraftRaw(raw);
        const first = Array.isArray(parsed) ? parsed[0] : parsed;

        return first?.blocks?.map((block) => block.text).join("\n") || "";
    } catch {
        return "";
    }
}

function getMcqOptions(questionContent) {
    try {
        const parsed = JSON.parse(questionContent || "[]");

        return parsed.slice(1).map((optionRaw, index) => ({
            index,
            raw: optionRaw,
            text: getDraftText(optionRaw),
        }));
    } catch {
        return [];
    }
}

function buildMcqDisplayData({ question, candidateAnswer, modelAnswerRaw }) {
    const options = getMcqOptions(question.content);
    const selectedIndexes = getMcqSelectedIndexes(candidateAnswer);
    const correctIndexes = getMcqCorrectIndexes(modelAnswerRaw);

    return {
        options,
        selectedIndexes,
        correctIndexes,
        candidateAnswerText: selectedIndexes
            .map((index) => options.find((option) => option.index === index)?.text)
            .filter(Boolean)
            .join(", "),
        modelAnswerText: correctIndexes
            .map((index) => options.find((option) => option.index === index)?.text)
            .filter(Boolean)
            .join(", "),
    };
}

function getAnswerDisplay(answerContent) {
    const parsed = parseAnswerContent(answerContent);

    if (!parsed) {
        return {
            text: "",
            textRaw: null,
            files: [],
        };
    }

    if (typeof parsed === "string") {
        return {
            text: parsed,
            textRaw: null,
            files: [],
        };
    }

    // New format from ExamPaperPage:
    // { text: DraftJSRaw, files: [...] }
    if (
        parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        ("text" in parsed || "files" in parsed)
    ) {
        const textRaw = parsed.text || null;

        return {
            text: textRaw ? getDraftText(textRaw) : "",
            textRaw,
            files: Array.isArray(parsed.files) ? parsed.files : [],
        };
    }

    // Old hybrid format:
    // [
    //   { part_type: "text", value: DraftJSRaw },
    //   { part_type: "file", blob: "file.pdf" }
    // ]
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.part_type) {
        let textRaw = null;
        const files = [];

        parsed.forEach((part) => {
            if (part.part_type === "text" && part.value) {
                textRaw = part.value;
            }

            if (part.part_type === "file" && part.blob) {
                files.push(part.blob);
            }
        });

        return {
            text: textRaw ? getDraftText(textRaw) : "",
            textRaw,
            files,
        };
    }

    // Old typed-only DraftJS raw answer
    if (parsed?.blocks) {
        return {
            text: getDraftText(parsed),
            textRaw: parsed,
            files: [],
        };
    }

    // Old upload-only answer
    if (Array.isArray(parsed)) {
        return {
            text: "",
            textRaw: null,
            files: parsed
                .map((file) =>
                    typeof file === "string"
                        ? file.replace(/"/g, "").trim()
                        : file
                )
                .filter(Boolean),
        };
    }

    return {
        text: "",
        textRaw: null,
        files: [],
    };
}

function getSelfGradingRows(structure = []) {
    return structure.flatMap((part) =>
        part.sections.flatMap((section) =>
            section.questionGroups.flatMap((group) => group.children)
        )
    );
}
export function ExamGradingProvider({ children }) {
    const [report, setReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [candidateAnswerMap, setCandidateAnswerMap] = useState({});
    const [loadedExamKey, setLoadedExamKey] = useState("");
    const [loadedReportKey, setLoadedReportKey] = useState("");
    const [autoMarksMap, setAutoMarksMap] = useState({});
    const [answerContentMap, setAnswerContentMap] = useState({});
    const [candidateAnswers, setCandidateAnswers] = useState([]);
    const [selfGradingQuestions, setSelfGradingQuestions] = useState([]);
    const [selfGradingSummary, setSelfGradingSummary] = useState(null);
    const [reportError, setReportError] = useState("");
    const [questions, setQuestions] = useState([]);
    const [gradeScales, setGradeScales] = useState([]);
    const [questionMix, setQuestionMix] = useState({
        hasMcq: false,
        hasManual: false,
        isMcqOnly: false,
        isManualOnly: false,
        isMixed: false,
        totalMarkAllocation: 0,
    });

    const loadGradeScales = useCallback(async () => {
        if (gradeScales.length > 0) {
            return gradeScales;
        }

        const response = await candidateService.getAllGradeScales();

        const scales =
            response?.data?.candidate_grade_scales ||
            response?.data?.data?.candidate_grade_scales ||
            [];

        setGradeScales(scales);
        return scales;
    }, [gradeScales]);

    const loadReport = useCallback(async (exam, options = {}) => {
        try {
            setReportLoading(true);
            setReportError("");

            const candidateId = exam?.candidate_id;
            const attemptNo = exam?.attempt_no || 1;
            const reportKey = `${candidateId}-${attemptNo}`;

            if (!options.force && loadedReportKey === reportKey && report) {
                return report;
            }

            if (!candidateId) {
                throw new Error("Candidate ID is missing.");
            }

            const response = await CandidateExamService.getReport({
                candidate_id: candidateId,
                attempt_no: attemptNo,
            });

            const reportData =
                response?.data?.assessment ||
                response?.data?.data?.assessment ||
                response?.data?.data ||
                response?.data ||
                null;

            setReport(reportData);
            setLoadedReportKey(reportKey);

            return reportData;
        } catch (error) {
            setReportError(error?.message || "Failed to load grade report.");
            setReport(null);
            return null;
        } finally {
            setReportLoading(false);
        }
    }, [loadedReportKey, report]);

    const loadGradingPaper = useCallback(async (exam) => {
        const assessmentId = exam?.assessment_id || exam?.id;
        const candidateId = exam?.candidate_id;
        const examKey = `${candidateId}-${exam?.attempt_no || 1}-${assessmentId}`;

        if (!assessmentId || !candidateId) return [];

        if (loadedExamKey === examKey && questions.length > 0) {
            return {
                questions,
                candidateAnswerMap,
                autoMarksMap,
                answerContentMap,
                assessment_details: exam?.assessment_details || null,
            };
        }

        const [paperResponse, answersResponse] = await Promise.all([
            //testService.loadAssessmentQuestions(assessmentId),
            CandidateExamService.loadAssessmentQuestions(assessmentId),
            CandidateExamService.getCandidateAnswers(candidateId),
        ]);

        const payload = paperResponse?.data?.data || paperResponse?.data || {};
        const parsedQuestions = applyDefaultPartSectionFallback(
            parseExamQuestions(payload.examQuestions)
        );
        const assessmentDetails = payload.assessment_details || null;
        const currentQuestionMix = getQuestionMix(parsedQuestions);

        const answers =
            answersResponse?.data?.answers ||
            answersResponse?.data?.data?.answers ||
            [];

        const answerMap = buildCandidateAnswerMap(answers);

        const answerContentResponse = await testService.getAllAnswerContents({
            questions: parsedQuestions,
        });

        const answerContents =
            answerContentResponse?.data ||
            answerContentResponse?.data?.data ||
            answerContentResponse ||
            [];
        const answerMapByQuestionId = {};

        parsedQuestions.forEach((question, index) => {
            answerMapByQuestionId[Number(question.question_id)] =
                answerContents[index] ?? question.answer ?? "";
        });

        setAnswerContentMap(answerMapByQuestionId);

        const mcqMarks = {};

        parsedQuestions.forEach((question, index) => {
            if (question.question_type !== QUESTION_TYPES.MCQ) return;

            mcqMarks[question.question_id] = calculateMcqMarks({
                question,
                candidateAnswer: answerMap[question.question_id],
                answerContent: answerContents[index],
            });
        });

        setQuestions(parsedQuestions);
        setQuestionMix(currentQuestionMix);
        setCandidateAnswerMap(answerMap);
        setCandidateAnswers(answers);
        setAutoMarksMap(mcqMarks);
        setLoadedExamKey(examKey);

        return {
            questions: parsedQuestions,
            candidateAnswerMap: answerMap,
            autoMarksMap: mcqMarks,
            answerContentMap: answerMapByQuestionId,
            assessment_details: assessmentDetails,
        };
    }, [loadedExamKey, questions, candidateAnswerMap, autoMarksMap, answerContentMap]);


    const loadCandidateAnswers = useCallback(async (candidateId) => {
        if (!candidateId) return [];

        const response = await CandidateExamService.getCandidateAnswers(candidateId);

        const answers =
            response?.data?.answers ||
            response?.data?.data?.answers ||
            response?.data ||
            [];

        setCandidateAnswers(answers);
        return answers;
    }, []);

    const buildSelfGradingDataset = useCallback((source = {}) => {
        const sourceQuestions = source.questions || questions;
        const sourceCandidateAnswerMap = source.candidateAnswerMap || candidateAnswerMap;
        const sourceAutoMarksMap = source.autoMarksMap || autoMarksMap;
        const sourceAnswerContentMap = source.answerContentMap || answerContentMap;

        
        const structure = buildPaperStructure({
            questions: sourceQuestions,
            candidateAnswerMap: sourceCandidateAnswerMap,
            autoMarksMap: sourceAutoMarksMap,
            answerContentMap: sourceAnswerContentMap,
        });

        const rows = getSelfGradingRows(structure);

        const totalMarks = rows.reduce(
            (sum, row) => sum + Number(row.markAllocation || 0),
            0
        );

        const currentMarks = rows.reduce(
            (sum, row) => sum + Number(row.markObtained || 0),
            0
        );

        setSelfGradingQuestions(structure);
        setSelfGradingSummary({
            total: rows.length,
            currentMarks,
            totalMarks,
            percentage: totalMarks
                ? `${Math.round((currentMarks / totalMarks) * 100)}%`
                : "-",
        });
        return rows;
    }, [questions, candidateAnswerMap, autoMarksMap, answerContentMap]);

    const saveSelfGradingMarks = useCallback(
        async ({ exam, marks }) => {
            const allRows = getSelfGradingRows(selfGradingQuestions);
            const questionsForSave = [];

            allRows.forEach((row) => {
                const existingAnswer = candidateAnswerMap[row.question_id];
                if (!existingAnswer) return;

                const value = marks[row.question_id];

                questionsForSave.push({
                    ...existingAnswer,
                    question_id: row.question_id,
                    mark_obtained: row.isLocked
                        ? Number(row.markObtained || 0)
                        : value === "" || value === null || value === undefined
                            ? null
                            : Number(value),
                    graded_by: row.isLocked
                        ? sessionStorage.getItem("user_role_id") || "root"
                        : value === "" || value === null || value === undefined
                            ? null
                            : sessionStorage.getItem("user_role_id") || "root",
                });
            });

            await gradeService.saveMarks({
                assessment_id: exam.assessment_id || exam.id,
                candidate_id: exam.candidate_id,
                attempt_no: exam.attempt_no || 1,
                user_role_id: sessionStorage.getItem("user_role_id") || "root",
                questions: questionsForSave,
                assessor_remarks: {
                    blocks: [],
                    entityMap: {},
                },
            });

            setLoadedExamKey("");

            setCandidateAnswerMap((prev) => {
                const next = { ...prev };

                questionsForSave.forEach((answer) => {
                    next[answer.question_id] = answer;
                });
                return next;
            });

            setSelfGradingQuestions((prev) =>
                prev.map((part) => ({
                    ...part,
                    sections: part.sections.map((section) => ({
                        ...section,
                        questionGroups: section.questionGroups.map((group) => ({
                            ...group,
                            children: group.children.map((row) => {
                                const savedAnswer = questionsForSave.find(
                                    (answer) =>
                                        Number(answer.question_id) === Number(row.question_id)
                                );

                                if (!savedAnswer) return row;

                                return {
                                    ...row,
                                    markObtained: savedAnswer.mark_obtained,
                                    candidateAnswer: {
                                        ...row.candidateAnswer,
                                        mark_obtained: savedAnswer.mark_obtained,
                                        graded_by: savedAnswer.graded_by,
                                    },
                                };
                            }),
                        })),
                    })),
                }))
            );
            return true;
        },
        [selfGradingQuestions, candidateAnswerMap]
    );
    const clearSelfGradingMarks = useCallback(
        async ({ exam }) => {
            const allRows = getSelfGradingRows(selfGradingQuestions);
            const questionsForSave = [];

            allRows.forEach((row) => {
                const existingAnswer = candidateAnswerMap[row.question_id];
                if (!existingAnswer) return;

                questionsForSave.push({
                    ...existingAnswer,
                    question_id: row.question_id,
                    mark_obtained: row.isLocked ? Number(row.markObtained || 0) : null,
                    graded_by: row.isLocked
                        ? sessionStorage.getItem("user_role_id") || "root"
                        : null,
                });
            });

            await gradeService.saveMarks({
                assessment_id: exam.assessment_id || exam.id,
                candidate_id: exam.candidate_id,
                attempt_no: exam.attempt_no || 1,
                user_role_id: sessionStorage.getItem("user_role_id") || "root",
                questions: questionsForSave,
                assessor_remarks: {
                    blocks: [],
                    entityMap: {},
                },
            });

            setLoadedExamKey("");

            setCandidateAnswerMap((prev) => {
                const next = { ...prev };

                questionsForSave.forEach((answer) => {
                    next[answer.question_id] = answer;
                });

                return next;
            });

            setSelfGradingQuestions((prev) =>
                prev.map((part) => ({
                    ...part,
                    sections: part.sections.map((section) => ({
                        ...section,
                        questionGroups: section.questionGroups.map((group) => ({
                            ...group,
                            children: group.children.map((row) => ({
                                ...row,
                                markObtained: row.isLocked ? row.markObtained : "",
                                candidateAnswer: {
                                    ...row.candidateAnswer,
                                    mark_obtained: row.isLocked ? row.markObtained : null,
                                    graded_by: row.isLocked
                                        ? sessionStorage.getItem("user_role_id") || "root"
                                        : null,
                                },
                            })),
                        })),
                    })),
                }))
            );

            return true;
        },
        [selfGradingQuestions, candidateAnswerMap]
    );

    const unpublishSelfGrade = useCallback(
        async ({ exam }) => {
            const response = await CandidateExamService.selfFinalizeGrade({
                candidate_id: exam.candidate_id,
                user_role_id: sessionStorage.getItem("user_role_id") || "root",
                final_grade: "",
                attempt_no: exam.attempt_no || 1,
                total_mark_allocation: null,
                total_mark_obtained: null,
                grade: null,
            });

            await loadReport(exam, { force: true });

            return response;
        },
        [loadReport]
    );

    const publishSelfGrade = useCallback(
        async ({ exam, marks }) => {
            const allRows = getSelfGradingRows(selfGradingQuestions);

            const totalMarkAllocation = allRows.reduce(
                (sum, row) => sum + Number(row.markAllocation || 0),
                0
            );

            const finalGrade = allRows.reduce(
                (sum, row) => sum + Number(marks[row.question_id] || 0),
                0
            );

            await saveSelfGradingMarks({ exam, marks });

            const response = await CandidateExamService.selfFinalizeGrade({
                candidate_id: exam.candidate_id,
                user_role_id: sessionStorage.getItem("user_role_id") || "root",
                final_grade: "grade",
                attempt_no: exam.attempt_no || 1,
                total_mark_allocation: totalMarkAllocation,
                total_mark_obtained: finalGrade,
                grade: null,
            });

            await loadReport(exam);

            return response;
        },
        [selfGradingQuestions, saveSelfGradingMarks, loadReport]
    );

    const getGradeByPercentage = useCallback(
        (percentage) => {
            return gradeScales.find(
                (scale) =>
                    Number(percentage) >= Number(scale.min_marks) &&
                    Number(percentage) <= Number(scale.max_marks)
            );
        },
        [gradeScales]
    );

    function buildPaperStructure({
        questions = [],
        candidateAnswerMap = {},
        autoMarksMap = {},
        answerContentMap = {},
    }) {
        const sortedQuestions = sortQuestionsForExam(questions);
        const mainQuestions = getMainQuestions(sortedQuestions);
        const subQuestionMap = getSubQuestionMap(sortedQuestions);
        const groupedBlocks = groupQuestionsByPartAndSection(mainQuestions);

        const mainQuestionIndexMap = mainQuestions.reduce((map, question, index) => {
            map[String(getQuestionId(question))] = index;
            return map;
        }, {});

        const buildRow = (question, index) => {
            const candidateAnswer = candidateAnswerMap[question.question_id];
            const isMcq = question.question_type === QUESTION_TYPES.MCQ;
            const isSubmission = question.question_type === QUESTION_TYPES.SUBMISSION;
            const markAllocation = Number(question.mark_allocation || 0);

            const modelAnswerRaw =
                answerContentMap[question.question_id] || question.answer || "";

            const modelAnswerFiles = isSubmission
                ? getSubmissionAttachments(modelAnswerRaw)
                : [];

            const candidateAnswerDisplay = getAnswerDisplay(
                candidateAnswer?.answer_content
            );

            const mcqDisplay = isMcq
                ? buildMcqDisplayData({
                    question,
                    candidateAnswer,
                    modelAnswerRaw,
                })
                : null;

            return {
                index,
                question_id: question.question_id,
                questionType: question.question_type,
                questionRaw: isMcq ? getMcqQuestionPrompt(question.content) : question.content,
                passageRaw: question.passage_content,
                modelAnswerRaw,
                modelAnswerFiles,
                modelAnswerText: isMcq
                    ? mcqDisplay?.modelAnswerText || ""
                    : getDraftText(modelAnswerRaw),
                guidelineRaw: question.guidence,

                candidateAnswerText: isMcq
                    ? mcqDisplay?.candidateAnswerText || ""
                    : candidateAnswerDisplay.text,
                candidateAnswerTextRaw: candidateAnswerDisplay.textRaw,
                candidateAnswerFiles: candidateAnswerDisplay.files,

                mcqOptions: mcqDisplay?.options || [],
                mcqSelectedIndexes: mcqDisplay?.selectedIndexes || [],
                mcqCorrectIndexes: mcqDisplay?.correctIndexes || [],

                markAllocation,
                markObtained:
                    candidateAnswer?.mark_obtained !== null &&
                        candidateAnswer?.mark_obtained !== undefined
                        ? Number(candidateAnswer.mark_obtained)
                        : candidateAnswer
                            ? isMcq
                                ? Number(autoMarksMap[question.question_id] || 0)
                                : ""
                            : "",

                isMcq,
                isLocked: isMcq,
                candidateAnswer,
                originalQuestion: question,
            };
        };

        return groupedBlocks.map((partGroup) => ({
            ...partGroup,
            sections: partGroup.sections.map((sectionGroup) => ({
                ...sectionGroup,
                questionGroups: sectionGroup.questions
                    .map((parentQuestion, parentIndex) => {
                        const realParentIndex =
                            mainQuestionIndexMap[String(getQuestionId(parentQuestion))] ??
                            parentIndex;

                        const childQuestions = parentQuestion.sub_questions
                            ? String(parentQuestion.sub_questions)
                                .split(",")
                                .map((id) => subQuestionMap[String(id.trim())])
                                .filter(Boolean)
                            : [];

                        const isStandaloneGradable =
                            Number(parentQuestion.mark_allocation || 0) > 0 &&
                            parentQuestion.question_type !== QUESTION_TYPES.SUBQUESTION;

                        const hasChildren = childQuestions.length > 0;

                        const children = hasChildren
                            ? childQuestions.map((child, childIndex) => buildRow(child, childIndex))
                            : isStandaloneGradable
                                ? [buildRow(parentQuestion, realParentIndex)]
                                : [];

                        return {
                            parentIndex: realParentIndex,
                            parentQuestionId: parentQuestion.question_id,

                            // only show parentRaw for main questions with subquestions
                            parentRaw: hasChildren ? parentQuestion.content : null,
                            passageId: parentQuestion.passage_id ?? null,
                            passageRaw: parentQuestion.passage_content,
                            children,
                        };
                    })
                    .filter((group) => group.children.length > 0),
            })),
        }));
    }

    const value = useMemo(
        () => ({
            report,
            reportLoading,
            reportError,
            loadReport,
            setReport,
            questions,
            questionMix,
            loadGradingPaper,
            candidateAnswerMap,
            autoMarksMap,
            // autoGradeMcqs,
            candidateAnswers,
            loadCandidateAnswers,
            selfGradingQuestions,
            selfGradingSummary,
            buildSelfGradingDataset,
            answerContentMap,
            saveSelfGradingMarks,
            clearSelfGradingMarks,
            publishSelfGrade,
            gradeScales,
            loadGradeScales,
            getGradeByPercentage,
            unpublishSelfGrade,
            isGradePublished:
                report?.final_grade !== null && report?.final_grade !== undefined,
        }),
        [report, reportLoading, reportError, loadReport, questions,
            questionMix,
            loadGradingPaper, candidateAnswerMap, // autoGradeMcqs,
            autoMarksMap, candidateAnswers,
            loadCandidateAnswers,
            selfGradingQuestions,
            selfGradingSummary,
            buildSelfGradingDataset, answerContentMap, saveSelfGradingMarks, clearSelfGradingMarks,
            publishSelfGrade, gradeScales,
            loadGradeScales,
            getGradeByPercentage,]
    );

    return (
        <ExamGradingContext.Provider value={value}>
            {children}
        </ExamGradingContext.Provider>
    );
}

export function useExamGradingContext() {
    const context = useContext(ExamGradingContext);

    if (!context) {
        throw new Error(
            "useExamGradingContext must be used inside ExamGradingProvider"
        );
    }

    return context;
}